/**
 * 目标台词高亮：预处理为数字索引，播放路径只做 O(n) 数值比较 + gap 段 O(log n) 二分查找，避免每帧解析时间字符串。
 */

import { parseTimeToSeconds } from '../../../utils/time.utils';

export type SubtitleHighlightRow = {
    id: number;
    originStart: number;
    originEnd: number;
    postStart: number | null;
    postEnd: number | null;
    displayText: string;
};

export type SubtitleHighlightIndex = {
    rows: SubtitleHighlightRow[];
    /** 原字幕数组下标，按「翻译轨主起点」排序（postStart ?? originStart），同起点按原顺序 */
    orderByTranslatedPrimaryStart: number[];
    /** 原字幕数组下标，按 originStart 排序 */
    orderByOriginStart: number[];
};

/** 与 videoEdit / time.utils parseTimeToSeconds 行为一致 */
export function parseSubtitleTimeToSeconds(timeString: string): number {
    return parseTimeToSeconds(timeString);
}

export function buildSubtitleHighlightIndex(subtitles: Task.SubtitlesLegacy[]): SubtitleHighlightIndex {
    const rows: SubtitleHighlightRow[] = subtitles.map((s) => ({
        id: s.id,
        originStart: parseSubtitleTimeToSeconds(s.origin_start_time),
        originEnd: parseSubtitleTimeToSeconds(s.origin_end_time),
        postStart: s.post_start_time ? parseSubtitleTimeToSeconds(s.post_start_time) : null,
        postEnd: s.post_end_time ? parseSubtitleTimeToSeconds(s.post_end_time) : null,
        displayText: s.post_text || s.origin_text || '',
    }));

    const indices = rows.map((_, i) => i);
    const translatedPrimary = (i: number) => rows[i].postStart ?? rows[i].originStart;

    const orderByTranslatedPrimaryStart = [...indices].sort((a, b) => {
        const sa = translatedPrimary(a);
        const sb = translatedPrimary(b);
        if (sa !== sb) return sa - sb;
        return a - b;
    });

    const orderByOriginStart = [...indices].sort((a, b) => {
        const sa = rows[a].originStart;
        const sb = rows[b].originStart;
        if (sa !== sb) return sa - sb;
        return a - b;
    });

    return { rows, orderByTranslatedPrimaryStart, orderByOriginStart };
}

function getTranslatedPrimaryStart(rows: SubtitleHighlightRow[], i: number): number {
    return rows[i].postStart ?? rows[i].originStart;
}

/**
 * 返回当前时间应对应高亮的字幕 id（与 index.tsx 原逻辑一致：重合取起点更晚者；空隙取「第一个起点≥当前时间」，否则最后一条）
 */
export function findSubtitleHighlightMatch(
    index: SubtitleHighlightIndex,
    timeInSeconds: number,
    useOriginTimes: boolean
): number | null {
    const { rows, orderByTranslatedPrimaryStart, orderByOriginStart } = index;
    if (rows.length === 0) {
        return null;
    }

    let bestId: number | null = null;
    let bestStart = -1;

    for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const isInOrigin = timeInSeconds >= r.originStart && timeInSeconds < r.originEnd;
        const isInPost =
            r.postStart !== null &&
            r.postEnd !== null &&
            timeInSeconds >= r.postStart &&
            timeInSeconds < r.postEnd;

        if (isInOrigin || isInPost) {
            const segmentStart = useOriginTimes ? r.originStart : getTranslatedPrimaryStart(rows, i);
            if (segmentStart > bestStart) {
                bestStart = segmentStart;
                bestId = r.id;
            }
        }
    }

    if (bestId !== null) {
        return bestId;
    }

    const order = useOriginTimes ? orderByOriginStart : orderByTranslatedPrimaryStart;
    const getStart = (rowIdx: number) =>
        useOriginTimes ? rows[rowIdx].originStart : getTranslatedPrimaryStart(rows, rowIdx);

    let lo = 0;
    let hi = order.length - 1;
    let foundOrderIdx = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const rowIdx = order[mid];
        const st = getStart(rowIdx);
        if (st >= timeInSeconds) {
            foundOrderIdx = mid;
            hi = mid - 1;
        } else {
            lo = mid + 1;
        }
    }

    if (foundOrderIdx >= 0) {
        return rows[order[foundOrderIdx]].id;
    }
    return rows[rows.length - 1].id;
}
